'use client';

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { instance } from 'src/api/request';
import { AUTH } from 'src/constants';
import storage from 'local-storage-fallback';
import useLocalStorage from 'src/hooks/system/useLocalStorage';
import { useLogging } from 'src/context/logging';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { shutdownIntercom } from 'src/components/IntercomWidget';
import { TARGET_STRATEGIES_READ } from 'src/components/PermissionGuard/checks';
import { useAppDispatch } from 'src/redux/hooks';
import { usersApi } from 'src/api/redux';
import { useGetCurrentUserQuery } from 'src/api/redux/users';
import { resetAllApiState } from 'src/redux/resetAllApiState';
import { buildApiLoginUrl } from 'src/lib/auth/constants';
import {
  clearApiError,
  setApiError,
  selectApiErrorReason,
} from 'src/redux/slices/apiErrorSlice';

interface AuthContextValue {
  hasAccessToken: boolean;
  setHasAccessToken: (value: boolean) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = (props) => {
  const [accessToken] = useLocalStorage(AUTH.ACCESS_TOKEN, '');
  const [hasAccessToken, setHasAccessToken] = useState(!!accessToken);

  // Sync hasAccessToken state with the stored access token
  useEffect(() => {
    setHasAccessToken(!!accessToken);
  }, [accessToken]);

  const contextValue = useMemo(
    () => ({ hasAccessToken, setHasAccessToken }),
    [hasAccessToken]
  );

  return <AuthContext.Provider value={contextValue} {...props} />;
};

/**
 * Hook to identify user with LaunchDarkly and logging services.
 * Should be called once in the app, typically in AuthorizedLayout.
 */
export const useIdentifyUser = () => {
  const { user } = useAuth();
  const ldClient = useLDClient();
  const log = useLogging();

  useEffect(() => {
    if (!ldClient) return;
    if (!user) return;
    if (!user.company) return;

    const company: Record<string, unknown> = {
      key: user.company.name,
      name: user.company.name,
      id: user.company.id,
    };

    // LD's `after` operator matches RFC-3339 strings or unix-millis. Only attach
    // created_at when present so a missing field degrades safely (date rules
    // won't match) instead of sending an invalid/empty value.
    if (user.company.created_at) {
      company.created_at = user.company.created_at;
    }

    ldClient.identify({
      kind: 'multi',
      user: { key: user.username, name: user.username },
      company,
    });

    if (!user.impersonation) log.identify(user);
  }, [user, ldClient, log]);
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  const { hasAccessToken, setHasAccessToken } = context;
  const dispatch = useAppDispatch();
  const [, setAccessToken] = useLocalStorage(AUTH.ACCESS_TOKEN, '');
  const [, setRefreshToken] = useLocalStorage(AUTH.REFRESH_TOKEN, '');
  const log = useLogging();

  // RTK Query is the single source of truth for user data
  const {
    data: user,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useGetCurrentUserQuery(undefined, {
    skip: !hasAccessToken,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: 120000, // Poll every 2 minutes
    skipPollingIfUnfocused: true, // Pause polling when tab is backgrounded
  });

  // setUser updates RTK Query cache directly
  const setUser = useCallback(
    (
      userOrUpdater:
        | MedScout.User
        | null
        | ((oldUser: MedScout.User) => MedScout.User)
    ) => {
      if (userOrUpdater === null) {
        // For logout, we reset the API state instead
        dispatch(usersApi.util.resetApiState());
        return;
      }

      dispatch(
        usersApi.util.updateQueryData('getCurrentUser', undefined, (draft) => {
          if (typeof userOrUpdater === 'function') {
            return userOrUpdater(draft);
          }
          return userOrUpdater;
        })
      );
    },
    [dispatch]
  );

  const login = useCallback(
    async ({
      access_token,
      refresh_token,
    }: {
      access_token: string | null;
      refresh_token: string | null;
    }) => {
      if (!access_token) {
        log.exception(new Error('No access token found in query params'));
        return '/search';
      }

      // Clear any stale API error from a previous session (e.g. a prior
      // account_blocked or rate_limited overlay) so the short-circuit in
      // baseQueryWithTokenHandling doesn't block this login attempt.
      dispatch(clearApiError());

      // Write tokens directly to storage so prepareHeaders can find them
      // for the initiate() call below. Do NOT flip hasAccessToken yet — that
      // would un-skip useGetCurrentUserQuery and fire every authenticated query
      // before we know whether the user is allowed in (race: a 401 from another
      // endpoint would redirect to logout before the 403 from auth/me can set
      // the account_blocked overlay).
      storage.setItem(AUTH.ACCESS_TOKEN, JSON.stringify(access_token));
      storage.setItem(AUTH.REFRESH_TOKEN, JSON.stringify(refresh_token));

      let redirectPath = '/search';
      try {
        // Fetch user via RTK Query — goes through baseQueryWithTokenHandling
        // which handles 403 (account_blocked) and populates the cache automatically
        const result = await dispatch(
          usersApi.endpoints.getCurrentUser.initiate(undefined, {
            forceRefetch: true,
          })
        ).unwrap();

        // useAuthorization reads this provider's user, so it can't run here;
        // check the freshly fetched user's codes directly.
        if (
          result?.has_strategies &&
          result?.all_permissions?.includes(TARGET_STRATEGIES_READ)
        ) {
          redirectPath = '/strategy-center';
        }
      } catch (err) {
        log.exception(err, {
          tags: { context: 'login user fetch' },
        });

        // baseQueryWithTokenHandling dispatches setApiError for 403/429, but
        // other failures (401, network error) don't. If no apiError was set,
        // dispatch account_blocked as a fallback so AuthorizedLayout shows
        // the overlay instead of a login redirect loop.
        dispatch((_, getState) => {
          if (!selectApiErrorReason(getState())) {
            dispatch(setApiError({ reason: 'account_blocked' }));
          }
        });
      }

      // Flip token state so AuthorizedLayout mounts — on success the RTK
      // Query cache is already populated; on failure the overlay shows.
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
      setHasAccessToken(true);

      return redirectPath;
    },
    [dispatch, setAccessToken, setRefreshToken, setHasAccessToken, log]
  );

  // Clears every piece of client session state. Tokens are removed through
  // `storage` (the same backend login writes to) so they're cleared even when
  // local-storage-fallback is using sessionStorage, cookies, or memory.
  const clearClientSession = useCallback(() => {
    resetAllApiState(dispatch);
    setAccessToken('');
    setRefreshToken('');
    storage.removeItem(AUTH.ACCESS_TOKEN);
    storage.removeItem(AUTH.REFRESH_TOKEN);
    setHasAccessToken(false);
    shutdownIntercom();
  }, [dispatch, setAccessToken, setRefreshToken, setHasAccessToken]);

  const logout = useCallback(
    async (params = { user_initiated: false }): Promise<string | null> => {
      const queryParams = new URLSearchParams();
      if (params?.user_initiated) queryParams.set('user_initiated', 'true');
      // In non-production, tell the API to redirect back to this origin
      // after Auth0 logout, instead of defaulting to production. (MKT-682)
      if (process.env.NEXT_PUBLIC_TARGET_ENV?.toLowerCase() !== 'production') {
        queryParams.set('returnTo', buildApiLoginUrl());
      }
      const queryProps = queryParams.toString() ? `?${queryParams.toString()}` : '';
      try {
        log.event('logout_initiated', {
          user_initiated: params.user_initiated,
        });

        const response = await instance.request({
          url: `${process.env.NEXT_PUBLIC_API_URL}v1/admin/logout${queryProps}`,
          method: 'GET',
        });

        log.event('logout_api_success', {
          status: response?.status,
          hasLogoutUrl: !!response?.data?.logoutUrl,
        });

        // Clear client state only after successful API call
        try {
          clearClientSession();
          log.event('client_state_cleared');
        } catch (stateError) {
          log.exception(stateError, {
            tags: {
              message: 'Error clearing client state during logout',
            },
          });
          throw stateError;
        }

        const fallbackUrl = buildApiLoginUrl();
        const logoutUrl = response?.data?.logoutUrl;

        // Validate logout URL to prevent open redirect attacks
        if (logoutUrl) {
          try {
            const url = new URL(logoutUrl);
            const allowedHosts = [
              new URL(process.env.NEXT_PUBLIC_API_URL).host,
              window.location.host,
              'auth.medscout.io', // Auth0 tenant for SSO logout
            ];

            if (allowedHosts.includes(url.host)) {
              return logoutUrl;
            }

            log.exception(new Error('Invalid logout URL rejected'), {
              tags: { logoutUrl, allowedHosts: allowedHosts.join(', ') },
            });
          } catch {
            log.exception(new Error('Malformed logout URL rejected'), {
              tags: { logoutUrl },
            });
          }
        }

        return fallbackUrl;
      } catch (err) {
        // Extract error message from various possible sources
        const errorMessage =
          err?.error_message || // API response error
          err?.message || // Axios/JS error
          err?.response?.data?.error_message || // Nested response
          'Unknown error';

        // If logout failed due to auth issues (401/403), the session is already invalid
        // Treat this as a successful logout - just clear local state and redirect
        const isAuthError =
          err?.response?.status === 401 ||
          err?.response?.status === 403 ||
          err?.status === 401 ||
          err?.status === 403;

        if (isAuthError) {
          log.event('logout_auth_error_treated_as_success', {
            status: err?.response?.status || err?.status,
          });

          // Clear local state even if API call failed
          try {
            clearClientSession();
          } catch (stateError) {
            log.exception(stateError, {
              tags: {
                message: 'Error clearing client state during auth-error logout',
              },
            });
          }

          // Return login URL - session was already invalid anyway
          return buildApiLoginUrl();
        }

        const error = new Error(`Logout failed: ${errorMessage}`);

        log.exception(error, {
          tags: {
            error: errorMessage,
            status: err?.response?.status || err?.status,
            url: `${process.env.NEXT_PUBLIC_API_URL}v1/admin/logout${queryProps}`,
          },
        });
        throw error;
      }
    },
    [clearClientSession, log]
  );

  // refreshLocalUser now just triggers RTK Query refetch. The request also
  // renews the stored tokens when the access token has expired. Resolves false
  // when there is no session to refresh or auth/me answers 401 (the tokens
  // can't be renewed). Other failures, such as a network error or a 5xx, say
  // nothing about the session, so they resolve true.
  const refreshLocalUser = useCallback(async (): Promise<boolean> => {
    if (!hasAccessToken) return false;
    const result = await refetch();
    const error = result?.error;
    // A 401 with a body that isn't JSON arrives as PARSING_ERROR.
    const status =
      error && 'status' in error
        ? error.status === 'PARSING_ERROR'
          ? error.originalStatus
          : error.status
        : undefined;
    return status !== 401;
  }, [hasAccessToken, refetch]);

  const hasAssignedTerritory =
    !!user?.assigned_territory && user?.assigned_territory !== null;

  return {
    user,
    isLoading,
    isFetching,
    error,
    isSuperUser: user?.is_superuser,
    hasAssignedTerritory,
    setUser,
    refreshLocalUser,
    login,
    logout,
  };
};

